import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import proptype from 'prop-types';

import { containerStyles } from '../../styles/layout';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { fontWeights } from '../../theme/type';
import { headers, richTextStyles } from '../../styles/type';
import { media } from '../../theme/media';

import TextRenderer from '../TextRenderer';
import Image from '../Image';
import IconCheckMark from '../../images/icons/check-mark.svg';

const Container = styled.div`
  padding: ${rem(40)} 0;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Subhead = styled.p`
  ${headers.h6}
  color: ${colors.blue['500']};
  text-align: center;
  margin-bottom: ${rem(20)};
  max-width: ${rem(800)};
`;

const Headline = styled.h2`
  ${headers.h3}
  text-align: center;
  color: ${colors.blue['800']};
  margin-bottom: ${rem(32)};
  max-width: ${rem(800)};
`;

const TabsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const Tabs = styled.div`
  display: flex;
  gap: ${rem(16)};
  overflow-x: auto;
  padding: 0 20px;
  margin: 0 ${rem(-20)} ${rem(32)} ${rem(-20)};

  ${media('>smedium')} {
    margin: 0 ${rem(-40)} ${rem(32)} ${rem(-40)};
  }
`;

const Tab = styled.button`
  padding: ${rem(12)} ${rem(18)};
  border-radius: ${rem(50)};
  background-color: ${colors.gray['50']};
  border: 1px solid ${colors.gray['200']};
  color: ${colors.gray['600']};
  font-size: ${rem(14)};
  font-weight: ${fontWeights.semiBold};
  white-space: nowrap;
  cursor: pointer;
  transition: color 200ms ease, background-color 200ms ease;

  ${media('>tiny')} {
    padding: ${rem(16)} ${rem(24)};
    font-size: ${rem(16)};
  }

  ${media('>small')} {
    padding: ${rem(20)} ${rem(32)};
    font-size: ${rem(20)};
  }

  &:hover {
    background-color: ${colors.blue['50']};
  }

  &[aria-selected='true'] {
    background-color: ${colors.blue['200']};
    border-color: ${colors.blue['200']};
    color: ${colors.blue['800']};
  }
`;

const TabPanel = styled.div`
  align-self: stretch;
  padding: ${rem(32)};
  border-radius: ${rem(8)};
  border: 1px solid ${colors.blue['100']};
  display: none;
  row-gap: ${rem(32)};
  column-gap: ${rem(48)};
  flex-direction: column;

  ${media('>small')} {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: ${rem(48)};
  }

  &[aria-hidden='false'] {
    display: flex;
  }
`;

const ImageContent = styled.div`
  max-width: ${rem(558)};

  ${media('>small')} {
    flex: 0 1 ${rem(558)};
  }
`;

const TextContent = styled.div`
  ${richTextStyles}
  max-width: ${rem(558)};

  ${media('>small')} {
    flex: 0 1 ${rem(558)};
    margin: auto 0;
  }

  & > ul {
    margin-left: ${rem(34)};

    & > li {
      position: relative;
      list-style: none;

      &::before {
        content: '';
        position: absolute;
        left: ${rem(-34)};
        top: ${rem(4)};
        display: inline-block;
        width: ${rem(18)};
        height: ${rem(13)};
        background-color: ${colors.blue['500']};
        mask-image: url(${IconCheckMark});
        mask-size: contain;
      }
    }
  }
`;

const MediaTabs = ({ subhead, headline, mediaTabs }) => {
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef([]);

  const handleKeyDown = (e, tabIndex) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      if (tabIndex > 0) {
        const nextTab = tabIndex - 1;
        setActiveTab(nextTab);
        tabRefs.current[nextTab].focus();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      if (tabIndex < mediaTabs.length - 1) {
        const nextTab = tabIndex + 1;
        setActiveTab(nextTab);
        tabRefs.current[nextTab].focus();
      }
    }
  };

  return (
    <Container>
      <ContentWrapper>
        {subhead && (
          <Subhead>
            <TextRenderer text={subhead} />
          </Subhead>
        )}
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        <TabsWrapper>
          <Tabs role="tablist">
            {mediaTabs.map(({ title }, i) => (
              title && (
                <Tab
                  id={`media-tab${i}`}
                  role="tab"
                  aria-selected={activeTab === i}
                  aria-controls={`media-tabpanel-${i}`}
                  tabIndex={activeTab === i ? 0 : -1}
                  ref={(el) => { tabRefs.current[i] = el; }}
                  onClick={() => setActiveTab(i)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                >
                  <TextRenderer text={title} />
                </Tab>
              )
            ))}
          </Tabs>
        </TabsWrapper>
        {mediaTabs.map(({ text, image }, i) => (
          <TabPanel
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            id={`media-tabpanel-${i}`}
            role="tabpanel"
            aria-labelledby={`media-tab${i}`}
            aria-hidden={activeTab !== i}
          >
            <ImageContent>
              {image && (
                <Image
                  image={image}
                  alt={image?.description}
                />
              )}
            </ImageContent>
            <TextContent>
              <TextRenderer text={text} richText />
            </TextContent>
          </TabPanel>
        ))}
      </ContentWrapper>
    </Container>
  );
};

MediaTabs.propTypes = {
  subhead: proptype.string,
  headline: proptype.string,
  mediaTabs: proptype.arrayOf(proptype.shape({
    title: proptype.string,
    // eslint-disable-next-line react/forbid-prop-types
    text: proptype.object,
    // eslint-disable-next-line react/forbid-prop-types
    image: proptype.object,
  })),
};

export const query = graphql`
  fragment MediaTabsFragment on ContentfulMediaTabs {
    id
    __typename
    subhead
    headline
    mediaTabs {
      title
      text {
        raw
      }
      image {
        gatsbyImageData(width: 558, quality: 75, resizingBehavior: FILL)
        description
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default MediaTabs;
